import { useCallback, useState } from "react";

import { EmptyState, ScorableTab, TabList, TabPanel, Tabs } from "@similarweb/ui-components";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { allTrackers } from "app/services/track/track";
import { swSettings } from "app/common/services/swSettings";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { searchAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/searchAdvertising/searchAdvertisingNavigator";
import { socialChannelsNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/social-channels/socialChannelsNavigator";

import type {
    ProWebsiteControllersApiWidgetAdIntelligenceChannelData,
    ProWebsiteControllersApiWidgetAdIntelligenceChannelMetric,
} from "@similarweb/pro-api-client";

import { EDataType } from "../../advertisers/overview/components/types";
import { AdIntelMetricsUnavailableBanner } from "../../common/banners/AdIntelMetricsUnavailableBanner";
import { useAdvertiserActivityDataAndParams } from "../../common/hooks/useAdvertiserActivityDataAndParams";
import { getErrorTypeFromTableNoDataType, getNoDataObject, NA_VALUE } from "../../common/utils/utils";

import { CHANNEL_CONFIG, CHANNEL_IDS, METRICS_CONFIG } from "./constants";
import { TabContent } from "./TabContent";
import { formatMetricValue } from "./TrackedActivityEstimatesConfigs";
import type { ChannelId, IChartData } from "./types";
import { useUnifiedChannelMetrics } from "./useUnifiedChannelMetrics";

const getMetricValue = (
    channelData: ProWebsiteControllersApiWidgetAdIntelligenceChannelData | undefined,
    metricType: EDataType,
): ProWebsiteControllersApiWidgetAdIntelligenceChannelMetric | null => {
    if (!channelData) {
        return null;
    }

    const metric = channelData[metricType];
    if (!metric) {
        return null;
    }

    return { value: metric.value ?? 0, change: metric.change ?? 0 };
};

export const TrackedActivityEstimates = () => {
    const params = useAdvertiserActivityDataAndParams();
    const [selectedTab, setSelectedTab] = useState<EDataType>(EDataType.SPEND);
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const { data, isPending, isError } = useUnifiedChannelMetrics();
    const hideSocial = isNoTouch(swSettings);

    const handleTabChange = useCallback((index: number) => {
        const selectedMetric = METRICS_CONFIG[index];
        setSelectedTab(selectedMetric);
        allTrackers.dispatchTrackEvent("Tab", "click", `Tracked Activity/${selectedMetric}`);
    }, []);

    const handleChannelClick = useCallback(
        (channelId: string) => {
            const navigationConfig = {
                [CHANNEL_IDS.display]: () =>
                    displayAdvertisingNavigation.overview.go({
                        ...params,
                        key: params.key,
                        duration: params.duration,
                    }),
                [CHANNEL_IDS.search]: () =>
                    searchAdvertisingNavigation.landingPages.go({
                        ...params,
                        key: params.key,
                        duration: params.duration,
                    }),
                [CHANNEL_IDS.social]: () =>
                    socialChannelsNavigation.promo.go({
                        key: params.key,
                        duration: params.duration,
                        country: params.country,
                    }),
            };

            const navigate = navigationConfig[channelId as keyof typeof navigationConfig];
            if (navigate) {
                allTrackers.dispatchTrackEvent("Internal Link", "click", `Tracked Activity/${channelId}`);
                void navigate();
            }
        },
        [params],
    );

    const getChartDataForMetric = useCallback(
        (metricType: EDataType): IChartData[] => {
            if (!data) {
                return [];
            }

            const channels = Object.entries(CHANNEL_CONFIG)
                .filter(([id]) => !(hideSocial && id === CHANNEL_IDS.social))
                .map(([id, config]) => {
                    const channelId = id as ChannelId;
                    let channelMetrics: ProWebsiteControllersApiWidgetAdIntelligenceChannelMetric | null = null;

                    if (channelId === CHANNEL_IDS.display) {
                        channelMetrics = getMetricValue(data.display, metricType);
                    } else if (channelId === CHANNEL_IDS.search) {
                        channelMetrics = getMetricValue(data.search, metricType);
                    }

                    return {
                        name: id,
                        id: channelId,
                        color: config.color,
                        channelData: channelMetrics,
                        hasNoMetricData: config.hasNoMetricData ?? false,
                    };
                });

            const activeChannels = channels.filter((channel) => !channel.hasNoMetricData);
            const total = activeChannels.reduce((sum, channel) => sum + (channel.channelData?.value ?? 0), 0);

            return channels.map((channel) => {
                const value = channel.channelData?.value ?? 0;
                const share = total > 0 && !channel.hasNoMetricData ? value / total : 0;

                return {
                    name: channel.name,
                    id: channel.id,
                    color: channel.color,
                    y: share,
                    share,
                    value,
                    change: channel.channelData?.change ?? 0,
                    visible: true,
                    metricType,
                };
            });
        },
        [data, hideSocial],
    );

    const showEmptyState =
        !isPending &&
        (!data ||
            Object.values(data.total).every(
                (metric) => !metric || Object.keys(metric).length === 0 || (metric.value === 0 && metric.change === 0),
            ));
    const { title, subtitle, type } = getNoDataObject(isError) ?? {};
    const showBanner = data?.hasAdIntelMetrics === false;

    return (
        <WidgetContainer
            title={translate("ad.intelligence.advertiser.activity.overview.tracked.activity.title")}
            tooltip={translate("ad.intelligence.advertiser.activity.overview.tracked.activity.title.tooltip")}
            subtitleFilters={subtitleFilters}
            isLoading={isPending}
            loaderWidth="100%"
            loaderHeight={240}
            noData={showEmptyState || isError}
            noDataTitle={title}
            noDataSubtitle={subtitle}
            noDataType={getErrorTypeFromTableNoDataType(type)}
            withBorder
        >
            <Stack>
                {showBanner ? (
                    <AdIntelMetricsUnavailableBanner
                        text={"display.advertising.adintelligence.display.metrics.unavailable.banner.message"}
                    />
                ) : null}
                <Tabs
                    selectedIndex={METRICS_CONFIG.indexOf(selectedTab as (typeof METRICS_CONFIG)[number])}
                    onSelect={handleTabChange}
                >
                    <TabList>
                        {METRICS_CONFIG.map((metric) => {
                            const totalMetric = getMetricValue(data?.total, metric);
                            const metricValue = totalMetric?.value ?? 0;
                            const metricChange = totalMetric?.change ?? 0;

                            return (
                                <ScorableTab
                                    hideBorder
                                    key={metric}
                                    metric={translate(
                                        `ad.intelligence.overview.performance.over.time.widget.${metric}`,
                                    )}
                                    value={formatMetricValue(metricValue, metric)}
                                    valueTooltip={translate(
                                        `ad.intelligence.advertiser.activity.overview.${metric}.tooltip`,
                                    )}
                                    valueChange={metricChange}
                                    valueChangeString={metricChange === 0 ? NA_VALUE : undefined}
                                />
                            );
                        })}
                    </TabList>
                    {METRICS_CONFIG.map((metric) => (
                        <TabPanel key={metric}>
                            {data?.total[metric]?.value === 0 && data?.total[metric]?.change === 0 ? (
                                <Flex
                                    justifyContent="center"
                                    alignItems="center"
                                    height="155px"
                                    padding="24px"
                                    boxSizing="content-box"
                                >
                                    <EmptyState
                                        title={title ?? ""}
                                        subtitle={subtitle}
                                    />
                                </Flex>
                            ) : (
                                <TabContent
                                    metricType={metric}
                                    chartData={getChartDataForMetric(metric)}
                                    onChannelClick={handleChannelClick}
                                />
                            )}
                        </TabPanel>
                    ))}
                </Tabs>
            </Stack>
        </WidgetContainer>
    );
};
